//Dependencies............................................
import { createContext, useContext } from "react";
import { FieldProps } from "@rjsf/utils";
import { Divider } from "antd";

//Components..............................................
import Conf30RulesField from "../../../../Components/VehicleConfigPageComp/Modals/CONF_3_0_Modal/CustomWidgets/Rules";
import Conf30CanMessageConfigField from "../../../../Components/VehicleConfigPageComp/Modals/CONF_3_0_Modal/CustomWidgets/CanMessageConfig"
import Conf30FunctionalityControlField from "../../../../Components/VehicleConfigPageComp/Modals/CONF_3_0_Modal/CustomWidgets/FunctionalityControl";
import Conf30CanBusConfigField from "../../../../Components/VehicleConfigPageComp/Modals/CONF_3_0_Modal/CustomWidgets/CanBusConfig";
import Conf30LinBusConfigField from "../../../../Components/VehicleConfigPageComp/Modals/CONF_3_0_Modal/CustomWidgets/LinBusConfig";
import Conf30LinMessageConfigField from "../../../../Components/VehicleConfigPageComp/Modals/CONF_3_0_Modal/CustomWidgets/LinMessageConfig";
import Conf30LinRulesField from "../../../../Components/VehicleConfigPageComp/Modals/CONF_3_0_Modal/CustomWidgets/LinRules";
import { displayToStored } from '../../../../utils/constants';

export const Conf30ActiveTabContext = createContext<string>('control');

export default function CONF_3_0_Modal (props: FieldProps) {
    const { formData, onChange, errorSchema, fieldPathId } = props;
    const activeTab = useContext(Conf30ActiveTabContext);
    // RJSF v6 requires the field's own path as onChange's 2nd argument.
    const rootOnChange = (updatedFormData: any) => onChange(updatedFormData, fieldPathId.path);

    const handleInvalidRules = (rules: any) => {
        const invalidRule = [
            {
                mask: [null],
                range_high: [null],
                range_low: [null]
            }
        ];
        if (JSON.stringify(rules) === JSON.stringify(invalidRule)) {
            return [];
        }

        return rules;
    };

    const handleCanBusConfigChange = (updatedCanBusConfig: any) => {
        const updatedCanMessageConfig = !updatedCanBusConfig.fd_enable
            ? Object.fromEntries(
                Object.entries(formData.can_message_config).map(([key]) => [
                    key,
                    {
                        ...formData.can_message_config[key],
                        dlc: formData.can_message_config[key]?.dlc <= 8 ? formData.can_message_config[key]?.dlc : displayToStored[8],
                        brs: false,
                    },
                ])
            )
            : formData.can_message_config;
        const updatedFormData = {
            ...formData,
            can_bus_config: updatedCanBusConfig,
            can_message_config: updatedCanMessageConfig,
        };
        rootOnChange(updatedFormData);
    };

    const handleCanMessageConfigChange = (updatedCanMessageConfig: any) => {
        const updatedFormData = {
            ...formData,
            can_message_config: updatedCanMessageConfig,
        };
        rootOnChange(updatedFormData);
    };

    const handleRulesChange = (updatedRules: any) => {
        const updatedFormData = {
            ...formData,
            rules: updatedRules,
        };
        rootOnChange(updatedFormData);
    };

    const handleLinBusConfigChange = (updatedLinBusConfig: any) => {
        const updatedFormData = {
            ...formData,
            lin_bus_config: updatedLinBusConfig,
        };
        rootOnChange(updatedFormData);
    };

    const handleLinMessageConfigChange = (updatedLinMessageConfig: any) => {
        const updatedFormData = {
            ...formData,
            lin_message_config: updatedLinMessageConfig,
        };
        rootOnChange(updatedFormData);
    };

    const handleLinSignalsChange = (updatedLinSignals: any) => {
        const updatedFormData = {
            ...formData,
            lin_signals: updatedLinSignals,
        };
        rootOnChange(updatedFormData);
    };

    const handleFunctionalityConfigChange = (updatedFunctionalityConfig: any) => {
        const updatedFormData = {
            ...formData,
            functionality_control: updatedFunctionalityConfig,
        };
        rootOnChange(updatedFormData);
    };

    return (
        <>
            <div style={{ display: activeTab === 'control' ? 'block' : 'none' }}>
                <Divider titlePlacement="left">CAN</Divider>
                <div style={{ marginBottom: '30px' }}>
                    <Conf30CanBusConfigField
                        formData={formData.can_bus_config}
                        onChange={handleCanBusConfigChange}
                        errorSchema={errorSchema}
                    />
                </div>
                <div style={{ marginBottom: '30px' }}>
                    <Conf30CanMessageConfigField
                        formData={formData.can_message_config}
                        formContext={formData}
                        onChange={handleCanMessageConfigChange}
                        errorSchema={errorSchema}
                    />
                </div>
                {/* CAN Rules and LIN Rules are two separate panels, but both
                    edit the same underlying rules[] array (tagged by `type`)
                    - that's what functionality_control's `rule` indices, the
                    schema, and the binary generator all key off of. */}
                <div style={{ marginBottom: '30px' }}>
                    <Conf30RulesField
                        ruleType="CAN"
                        formData={handleInvalidRules(formData.rules)}
                        formContext={formData}
                        onChange={handleRulesChange}
                        errorSchema={errorSchema}
                    />
                </div>
                <Divider titlePlacement="left">LIN</Divider>
                <div style={{ marginBottom: '30px' }}>
                    <Conf30LinBusConfigField
                        formData={formData.lin_bus_config}
                        onChange={handleLinBusConfigChange}
                    />
                </div>
                <div style={{ marginBottom: '30px' }}>
                    <Conf30LinMessageConfigField
                        formData={formData.lin_message_config}
                        onChange={handleLinMessageConfigChange}
                        errorSchema={errorSchema}
                        linSignals={formData.lin_signals || []}
                        onSignalsChange={handleLinSignalsChange}
                    />
                </div>
                <div style={{ marginBottom: '30px' }}>
                    <Conf30LinRulesField
                        formData={handleInvalidRules(formData.rules)}
                        formContext={formData}
                        onChange={handleRulesChange}
                    />
                </div>
            </div>
            <div style={{ display: activeTab === 'functionality' ? 'block' : 'none' }}>
                <Conf30FunctionalityControlField
                    formData={formData.functionality_control}
                    formContext={formData}
                    onChange={handleFunctionalityConfigChange}
                />
            </div>
        </>
    );
};
